import { getFeatureConfig } from '@luna/feature-config';
import { getToken } from '@shared/auth';
import { COUNTRY_URL_PARAM } from '@shared/environment';
import { stripHashAndSlash } from '@luna/router';

import { API_TARGET_URL_PARAM } from './ApiTargetOverrideService';

export type EnvironmentSwitchUrlBuilder = (origin: string, apiTarget?: string) => string;

const ACCESS_TOKEN_URL_PARAM = 'at';
const LOCALHOST_HOSTNAME = 'localhost';
const PATHNAME_URL_PARAM = 'pathname';

function isLocalhost(origin: string): boolean {
    return new URL(origin).hostname === LOCALHOST_HOSTNAME;
}

function appendSchema(origin: string): string {
    if (origin.startsWith('http')) {
        return origin;
    }
    return `https://${origin}`;
}

export function buildEnvironmentSwitchUrl(origin: string, apiTarget?: string): string {
    const { country } = getFeatureConfig((config) => config.base);
    const { hash, pathname, search } = window.location;

    const originWithSchema = appendSchema(origin);
    const url = new URL(originWithSchema);

    const searchParams = new URLSearchParams(search);

    for (const [key, value] of url.searchParams) {
        searchParams.set(key, value);
    }

    if (isLocalhost(originWithSchema)) {
        searchParams.set(COUNTRY_URL_PARAM, country);
    } else {
        searchParams.delete(COUNTRY_URL_PARAM);
    }

    if (apiTarget) {
        // For backwards compatibility with older FE versions
        // which used different available targets map with different keys
        // Can be removed in time
        const legacyApiTarget = apiTarget.toLowerCase().replaceAll(' ', '');
        searchParams.set(API_TARGET_URL_PARAM, legacyApiTarget);
    }

    const normalizedPathname = stripHashAndSlash(pathname);
    if (normalizedPathname) {
        searchParams.set(PATHNAME_URL_PARAM, normalizedPathname);
    }

    searchParams.delete(ACCESS_TOKEN_URL_PARAM);

    const hashParams = new URLSearchParams(url.hash.slice(1));

    hashParams.append('access_token', getToken());

    url.search = searchParams.toString();
    url.hash = `${hashParams.toString()}&${hash}`;

    return url.toString();
}
